<script setup lang="ts">
import { computed, type ComputedRef, type defineComponent, type Ref } from 'vue'
import router from '@/router'
import { useRoutePath } from '@/composables/helpers/route-path'
import { RouteName } from '@/routes'
import { usePlayer } from '@/composables/game/player'
import { showPocPage } from '@/composables/feature-flag'
import { T } from '@tolgee/vue'
import { capitalizeFirstLetter } from '@/services/helper'
import GiftIcon from '@/components/icons/GiftIcon.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { twMerge } from 'tailwind-merge'
import SparkIcon from '@/components/icons/SparkIcon.vue'
import { useTaskList } from '@/composables/game/tasks'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import NavbarHomeImage from '@/components/navbar-icons/NavbarHomeImage.vue'
import NavbarLeafImage from '@/components/navbar-icons/NavbarLeafImage.vue'
import NavbarFriendsImage from '@/components/navbar-icons/NavbarFriendsImage.vue'
import NavbarMoneyImage from '@/components/navbar-icons/NavbarMoneyImage.vue'
import { useNewUpgradesChecker } from '@/composables/game/upgrade'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'

type NavbarMenuItem = {
	name: string,
	icon: string,
	iconClass?: string
	route: string,
	count?: ComputedRef<number>
	isSpecial?: boolean
	hasFingerEmoji?: Ref<boolean>
}
const currentRoutePath = computed(() => router.currentRoute.value.path)
const { getPathByName } = useRoutePath()
const { addHapticFeedback } = useHapticFeedback()
const { undoneTasks } = useTaskList()
const { newUpgrades } = useNewUpgradesChecker()
const {
  canShowOnboardingEmoji,
  showOnboardingEmojiNavbarHome,
  showOnboardingEmojiNavbarImprove,
  showOnboardingEmojiNavbarQuests,
  showOnboardingEmojiNavbarFriends,
} = useOnboardingFingerEmojis()
const { bottomNumber } = useFullScreenDimensions()

const iconComponents: Record<string, ReturnType<typeof defineComponent> | null> = {
  [RouteName.HOME]: NavbarHomeImage,
  [RouteName.MINE]: NavbarLeafImage,
  [RouteName.FRIENDS]: NavbarFriendsImage,
  [RouteName.EARN]: NavbarMoneyImage,
  [RouteName.REWARDS]: GiftIcon,
}

const { chosenToken } = usePlayer()
const undoneTasksCount = computed(() => undoneTasks.value.length)
const newUpgradesCount = computed(() => newUpgrades.value.length)

const menuItems = computed((): NavbarMenuItem[] => {
  return [
    {
      name: 'home',
      icon: iconComponents[RouteName.HOME],
      route: getPathByName(RouteName.HOME),
      hasFingerEmoji: showOnboardingEmojiNavbarHome,
    },
    {
      name: 'improve',
      icon: iconComponents[RouteName.MINE],
      route: getPathByName(RouteName.MINE),
      count: newUpgradesCount,
      hasFingerEmoji: showOnboardingEmojiNavbarImprove,
    },
    {
      name: 'rewards',
      icon: iconComponents[RouteName.REWARDS],
      route: getPathByName(RouteName.REWARDS),
      iconClass: 'opacity-100',
      isSpecial: true,
    },
    {
      name: 'quests',
      icon: iconComponents[RouteName.EARN],
      route: getPathByName(RouteName.EARN),
      count: undoneTasksCount,
      hasFingerEmoji: showOnboardingEmojiNavbarQuests,
    },
    ...(showPocPage.value ? [
      {
        name: 'poc',
        icon: iconComponents[RouteName.REWARDS],
        route: getPathByName(RouteName.POC),
      },
    ] : []),
    {
      name: 'friends',
      icon: iconComponents[RouteName.FRIENDS],
      route: getPathByName(RouteName.FRIENDS),
      hasFingerEmoji: showOnboardingEmojiNavbarFriends,
    },
  ]
})

const fullScreenStyles = computed(() => {
  return { height: `${bottomNumber.value + 64}px` }
})

function goToRoute(menuItem: NavbarMenuItem) {
  if (menuItem.route === getPathByName(RouteName.HOME)) {
    showOnboardingEmojiNavbarHome.value = false
  } else if (menuItem.route === getPathByName(RouteName.MINE)) {
    showOnboardingEmojiNavbarImprove.value = false
  } else if (menuItem.route === getPathByName(RouteName.EARN)) {
    showOnboardingEmojiNavbarQuests.value = false
  } else if (menuItem.route === getPathByName(RouteName.FRIENDS)) {
    showOnboardingEmojiNavbarFriends.value = false
  }
  addHapticFeedback('light')
  router.push(menuItem.route)
}

function isActive(menuItem: NavbarMenuItem) {
  return menuItem.route === currentRoutePath.value
}
</script>

<template>
  <div
    class="z-[28] w-full fixed bottom-0 max-w-[440px] bg-dark-primary flex flex-col touch-none"
    :style="fullScreenStyles"
  >
    <div class="flex flex-col">
      <div class="w-full h-px bg-gradient-border" />
      <div class="w-full h-full flex-1 px-4 flex items-center justify-between">
        <div
          class="w-[72px] h-full flex flex-col items-center justify-center cursor-pointer text-white relative"
          :class="{'-translate-y-4': menuItem.isSpecial}"
          v-for="menuItem in menuItems"
          :key="menuItem.name"
          @click="goToRoute(menuItem)"
        >
          <Transition
            name="fade"
          >
            <img
              v-if="canShowOnboardingEmoji && menuItem.hasFingerEmoji?.value"
              class="pointer-events-none absolute -top-6 left-[26px] size-[60px] rotate-[227deg] object-cover z-[1]"
              :src="FingerUpGif"
            >
          </Transition>
          <div
            :class="twMerge('w-8 h-8 min-w-[32px] flex items-center justify-center relative overflow-visible',
                            menuItem.isSpecial && 'size-[90px] min-w-[32px] pulsing-icon')"
          >
            <!--          when isSpecial:-->
            <div
              v-if="false"
              class="absolute h-[40px] w-[40px] blur-[16px] rounded-full bg-[#D8FF8AA8]"
            />
            <!--          when isSpecial:-->
            <SparkIcon
              v-if="false"
              class="absolute sparkling-star"
            />
            <component
              :is="menuItem.icon"
              class="w-auto h-full"
              :class="twMerge(
                'flex items-center justify-center',
                menuItem.iconClass,
                menuItem.isSpecial && 'mb-4 text-teal-primary',
              )"
              :token="chosenToken"
            />
            <div
              v-if="!!menuItem.count?.value && menuItem.count.value > 0"
              class="absolute -top-1.5 h-4 min-w-[16px] rounded-full bg-teal-primary flex items-center justify-center text-black text-[10px] leading-6 font-semibold"
              :class="menuItem.count.value > 9 ? '-right-2 pl-[3.5px] pr-[2.5px]' : '-right-1'"
            >
              {{ menuItem.count.value > 9 ? '9+' : menuItem.count.value }}
            </div>
          </div>
          <div
            v-if="!menuItem.isSpecial"
            class="text-sm leading-5 font-semibold -tracking-[0.28px]"
            :class="isActive(menuItem) ? 'opacity-100' : 'opacity-60'"
          >
            <T
              :key-name="`menu.${menuItem.name}`"
              :default-value="capitalizeFirstLetter(menuItem.name)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-gradient-border {
	background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 100) 50%, rgba(255, 255, 255, 0));
}

.pulsing-icon {
	opacity: 1;
	animation: pulse 2.5s infinite ease-in-out;
}

.sparkling-star {
	top: -2px;
	right: -2px;
	animation: sparkle 2.5s infinite;
	transition: all;
	transition-delay: 200ms;
}

@keyframes pulse {
	0% {
		scale: 1.1; /* Start normal size */
	}
	50% {
		scale: 1; /* Return to normal size */
	}
	100% {
		scale: 1.1; /* Return to normal size */
	}
}

@keyframes sparkle {
	0% {
		bottom: auto;
		left: auto;
		top: -2px;
		right: -2px;
		opacity: 1;
	}
	20% {
		bottom: auto;
		left: auto;
		top: -2px;
		right: -2px;
		opacity: 0;
	}
	35% {
		top: auto;
		right: auto;
		bottom: -2px;
		left: -2px;
		opacity: 0;
	}
	50% {
		top: auto;
		right: auto;
		bottom: -2px;
		left: -2px;
		opacity: 1;
	}
	70% {
		top: auto;
		right: auto;
		bottom: -2px;
		left: -2px;
		opacity: 0;
	}
	85% {
		bottom: auto;
		left: auto;
		top: -2px;
		right: -2px;
		opacity: 0;
	}
	100% {
		bottom: auto;
		left: auto;
		top: -2px;
		right: -2px;
		opacity: 1;
	}
}
</style>
